import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, radius, spacing, typography } from '../../config/theme';
import { ErrorBanner, Field, PrimaryButton, TextButton, Toggle } from '../ui';

const num = (value, fallback) => {
  const parsed = parseInt(String(value).trim(), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

const offs = (basis, offsetMinutes) => ({ basis, offsetMinutes, localTime: null, dayOffset: 0 });
const wall = (localTime) => ({ basis: 'absolute_local_time', offsetMinutes: null, localTime, dayOffset: 0 });

// Read a timing point back into the form's fields.
const readMode = (timing) => {
  const b = timing?.available?.basis;
  if (b === 'absolute_local_time') return 'clock';
  if (b === 'shift_end') return 'end';
  return 'start';
};

// One sheet for both "add task" and "edit task". `task` is null when adding.
export default function TaskEditorSheet({ visible, task, onClose, onSubmit }) {
  const initial = useMemo(() => {
    const mode = readMode(task?.timing);
    return {
      title: task?.title || '',
      order: String(task?.order ?? ''),
      mode, // 'start' | 'end' | 'clock'
      availOffset: String(task?.timing?.available?.offsetMinutes ?? (mode === 'end' ? -30 : 0)),
      dueOffset: task?.timing?.due?.offsetMinutes != null ? String(task.timing.due.offsetMinutes) : (mode === 'end' ? '0' : ''),
      availClock: task?.timing?.available?.localTime || '12:00',
      dueClock: task?.timing?.due?.localTime || '12:30',
      warnMin: String(task?.timing?.warningLeadMinutes ?? 15),
      photoRequired: !!task?.requirements?.photoRequired,
      amountRequired: !!task?.requirements?.amountRequired,
      commentRequired: !!task?.requirements?.commentRequired,
      blocksShiftClosing: !!task?.blocksShiftClosing,
    };
  }, [task]);

  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const [seed, setSeed] = useState(task?.id || 'new');
  if (visible && seed !== (task?.id || 'new')) { setSeed(task?.id || 'new'); setForm(initial); setError(null); }

  const buildTiming = () => {
    if (form.mode === 'clock') {
      if (!HHMM_RE.test(form.availClock)) throw new Error('Время доступности в формате ЧЧ:ММ');
      const hasDue = form.dueClock.trim() !== '';
      if (hasDue && !HHMM_RE.test(form.dueClock)) throw new Error('Время срока в формате ЧЧ:ММ');
      return { available: wall(form.availClock), due: hasDue ? wall(form.dueClock) : null, warningLeadMinutes: hasDue ? num(form.warnMin, 15) : null };
    }
    const basis = form.mode === 'end' ? 'shift_end' : 'shift_start';
    const a = num(form.availOffset, 0);
    const hasDue = form.dueOffset.trim() !== '';
    return { available: offs(basis, a), due: hasDue ? offs(basis, num(form.dueOffset, a + 30)) : null, warningLeadMinutes: hasDue ? num(form.warnMin, 15) : null };
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Введите название задачи'); return; }
    let timing;
    try { timing = buildTiming(); } catch (e) { setError(e.message); return; }
    const payload = {
      title: form.title.trim(),
      order: num(form.order, (task?.order ?? 1)),
      timing,
      requirements: {
        photoRequired: form.photoRequired,
        amountRequired: form.amountRequired,
        commentRequired: form.commentRequired,
      },
      blocksShiftClosing: form.blocksShiftClosing,
    };
    setBusy(true);
    setError(null);
    try {
      await onSubmit(payload);
      onClose();
    } catch (e) {
      setError(e?.serverMessage || (e?.code ? `Отклонено сервером: ${e.code}` : 'Не удалось сохранить'));
    } finally {
      setBusy(false);
    }
  };

  const MODES = [['start', 'От старта'], ['clock', 'В время'], ['end', 'От конца']];

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.head}>
            <Text style={styles.title}>{task ? 'Задача' : 'Новая задача'}</Text>
            <TextButton title="Отмена" kind="muted" onPress={onClose} />
          </View>
          <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">
            <ErrorBanner message={error} />
            <Field label="Название" value={form.title} onChangeText={(title) => set({ title })} placeholder="Пересчитать кассу" />
            <Field label="Порядок" value={form.order} onChangeText={(order) => set({ order })} keyboardType="number-pad" placeholder="1" />

            <Text style={styles.groupLabel}>КОГДА ВЫПОЛНЯТЬ</Text>
            <View style={styles.modes}>
              {MODES.map(([m, label]) => (
                <Pressable key={m} onPress={() => set({ mode: m })} style={[styles.modeChip, form.mode === m && styles.modeChipOn]}>
                  <Text style={[styles.modeText, form.mode === m && styles.modeTextOn]}>{label}</Text>
                </Pressable>
              ))}
            </View>

            {form.mode === 'clock' ? (
              <View style={styles.rowFields}>
                <View style={{ flex: 1 }}><Field label="Доступна с" value={form.availClock} onChangeText={(availClock) => set({ availClock })} placeholder="12:00" /></View>
                <View style={{ flex: 1 }}><Field label="Срок до" value={form.dueClock} onChangeText={(dueClock) => set({ dueClock })} placeholder="без срока" /></View>
              </View>
            ) : (
              <View style={styles.rowFields}>
                <View style={{ flex: 1 }}><Field label={form.mode === 'end' ? 'Мин от конца (−30)' : 'Мин от старта'} value={form.availOffset} onChangeText={(availOffset) => set({ availOffset })} keyboardType="numbers-and-punctuation" /></View>
                <View style={{ flex: 1 }}><Field label={form.mode === 'end' ? 'Срок, мин от конца' : 'Срок, мин от старта'} value={form.dueOffset} onChangeText={(dueOffset) => set({ dueOffset })} keyboardType="numbers-and-punctuation" placeholder="без срока" /></View>
              </View>
            )}
            <Field label="Предупредить за, мин" value={form.warnMin} onChangeText={(warnMin) => set({ warnMin })} keyboardType="number-pad" />

            <Text style={styles.groupLabel}>ЧТО ОБЯЗАТЕЛЬНО</Text>
            <ToggleRow label="Фото" value={form.photoRequired} onChange={(photoRequired) => set({ photoRequired })} />
            <ToggleRow label="Сумма" value={form.amountRequired} onChange={(amountRequired) => set({ amountRequired })} />
            <ToggleRow label="Комментарий" value={form.commentRequired} onChange={(commentRequired) => set({ commentRequired })} />
            <ToggleRow label="Блокирует закрытие смены" value={form.blocksShiftClosing} onChange={(blocksShiftClosing) => set({ blocksShiftClosing })} accent />

            <PrimaryButton title={task ? 'Сохранить' : 'Добавить задачу'} onPress={submit} loading={busy} />
            <Text style={styles.note}>Любое изменение поднимает версию набора; смены, которые уже идут, не затрагиваются.</Text>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function ToggleRow({ label, value, onChange, accent }) {
  return (
    <Pressable onPress={() => onChange(!value)} style={styles.toggleRow}>
      <Text style={[styles.toggleLabel, accent && value && { color: colors.warning }]}>{label}</Text>
      <Toggle value={value} onValueChange={onChange} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '90%' },
  head: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  title: { ...typography.h2, color: colors.textPrimary },
  rowFields: { flexDirection: 'row', gap: spacing.md },
  groupLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700', letterSpacing: 1, marginTop: spacing.xs },
  modes: { flexDirection: 'row', gap: spacing.sm },
  modeChip: { flex: 1, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingVertical: spacing.sm, alignItems: 'center' },
  modeChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  modeText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  modeTextOn: { color: colors.primary },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  toggleLabel: { ...typography.body, color: colors.textPrimary },
  note: { ...typography.caption, color: colors.textMuted },
});
